import { useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { LOGO_URL } from "@/lib/api";
import { LayoutDashboard, FileText, Receipt, ScrollText, Users, Truck, IdCard, LogOut, UserCog, Images, MessageSquare, Globe, Menu, X, FileSpreadsheet, Wallet, Settings, FileCheck2 } from "lucide-react";

const nav = [
  { to: "/portal", label: "Dashboard", icon: LayoutDashboard, testId: "nav-dashboard" },
  { to: "/portal/consignments", label: "Consignment Notes", icon: FileText, testId: "nav-consignments" },
  { to: "/portal/bills", label: "Freight Bills", icon: Receipt, testId: "nav-bills" },
  { to: "/portal/memos", label: "Freight Memos", icon: ScrollText, testId: "nav-memos" },
  { to: "/portal/pod", label: "Proof of Delivery", icon: FileCheck2, testId: "nav-pod" },
  { to: "/portal/masters/parties", label: "Parties", icon: Users, testId: "nav-parties" },
  { to: "/portal/masters/trucks", label: "Trucks", icon: Truck, testId: "nav-trucks" },
  { to: "/portal/masters/drivers", label: "Drivers", icon: IdCard, testId: "nav-drivers" },
  { to: "/portal/reports", label: "Excel Reports", icon: FileSpreadsheet, testId: "nav-reports" },
  { to: "/portal/payments", label: "Pending Payments", icon: Wallet, testId: "nav-payments" },
  { to: "/portal/site/gallery", label: "Gallery Manager", icon: Images, testId: "nav-site-gallery" },
  { to: "/portal/site/inquiries", label: "Inquiries", icon: MessageSquare, testId: "nav-site-inquiries" },
];

function Brand() {
  return (
    <div className="flex items-center gap-3">
      <img src={LOGO_URL} alt="JJ Logistics" className="w-11 h-11 object-contain" />
      <div>
        <div className="font-display text-lg leading-tight text-[#0b5cc4]">JAI JAGDAMBA</div>
        <div className="text-[11px] tracking-widest text-[#d4a017] font-semibold">LOGISTICS</div>
        <div className="text-[10px] italic text-slate-500 mt-0.5">We carry your trust</div>
      </div>
    </div>
  );
}

function SidebarContent({ user, onNavigate, onLogout }) {
  return (
    <>
      <nav className="p-3 space-y-1 flex-1 overflow-y-auto">
        {nav.map(({ to, label, icon: Icon, testId }) => (
          <NavLink
            key={to}
            to={to}
            end={to === "/portal"}
            onClick={onNavigate}
            className={({ isActive }) => `sidebar-link ${isActive ? "active" : ""}`}
            data-testid={testId}
          >
            <Icon size={18} />
            <span className="text-sm">{label}</span>
          </NavLink>
        ))}
        {user?.role === "admin" && (
          <NavLink to="/portal/users" onClick={onNavigate} className={({ isActive }) => `sidebar-link ${isActive ? "active" : ""}`} data-testid="nav-users">
            <UserCog size={18} /><span className="text-sm">Users</span>
          </NavLink>
        )}
        <NavLink to="/" onClick={onNavigate} className="sidebar-link" data-testid="nav-view-website">
          <Globe size={18} /><span className="text-sm">Public Website</span>
        </NavLink>
      </nav>
      <div className="p-3 border-t border-slate-100">
        <NavLink to="/portal/settings" onClick={onNavigate} className={({ isActive }) => `sidebar-link mb-1 ${isActive ? "active" : ""}`} data-testid="nav-settings">
          <Settings size={18} /><span className="text-sm">Account Settings</span>
        </NavLink>
        <div className="px-3 py-2 text-xs text-slate-500">
          Signed in as <div className="text-slate-800 font-medium truncate" data-testid="current-user-email">{user?.email}</div>
        </div>
        <button onClick={onLogout} className="sidebar-link w-full text-left" data-testid="logout-btn">
          <LogOut size={18} /><span className="text-sm">Logout</span>
        </button>
      </div>
    </>
  );
}

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const close = () => setOpen(false);

  const onLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen" data-testid="app-layout">
      {/* Desktop fixed sidebar */}
      <aside className="hidden lg:flex fixed inset-y-0 left-0 w-64 h-screen bg-white border-r border-slate-200 flex-col z-40 no-print">
        <div className="px-5 py-4 border-b border-slate-100">
          <Brand />
        </div>
        <SidebarContent user={user} onLogout={onLogout} />
      </aside>

      {/* Mobile top bar */}
      <header className="lg:hidden sticky top-0 z-40 h-16 bg-white border-b border-slate-200 flex items-center justify-between px-4 no-print" data-testid="portal-mobile-header">
        <Brand />
        <button onClick={() => setOpen(true)} className="p-2 text-slate-700" aria-label="Open menu" data-testid="portal-hamburger-btn">
          <Menu size={24} />
        </button>
      </header>

      {/* Mobile drawer */}
      <div className={`fixed inset-0 z-50 lg:hidden no-print transition-opacity duration-300 ${open ? "opacity-100" : "opacity-0 pointer-events-none"}`}>
        <div className="absolute inset-0 bg-slate-950/50 backdrop-blur-sm" onClick={close} />
        <div className={`absolute inset-y-0 left-0 w-72 bg-white flex flex-col transform transition-transform duration-300 ${open ? "translate-x-0" : "-translate-x-full"}`} data-testid="portal-mobile-drawer">
          <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
            <Brand />
            <button onClick={close} className="p-1.5 text-slate-500" aria-label="Close menu" data-testid="portal-drawer-close-btn">
              <X size={20} />
            </button>
          </div>
          <SidebarContent user={user} onNavigate={close} onLogout={onLogout} />
        </div>
      </div>

      {/* Main workspace: independent scroll on desktop */}
      <main className="portal-main lg:ml-64 lg:h-screen lg:overflow-y-auto overflow-x-hidden">
        <Outlet />
      </main>
    </div>
  );
}
